import {
  EnquiryHandle,
  IServerSideGetRowsParams,
  ServerSideCriteria,
  useServerEnquiry,
} from '@access-erp/common';
import { ConfirmationModal, FlexProps, ModalType } from '@access-erp/ui-components';
import { useRef, useState } from 'react';

import useCreateUsageHeader from './hooks/useCreateUsageHeader';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { ImportDocumentWithId } from '@/utils/data-helpers/types';
import { useHeaderColumns } from './columns/useHeaderColumns';
import useGetUsageHeaders from './hooks/useGetUsageHeaders';
import useVarBillingNavigation from './hooks/useVarBillingNavigation';
import { useAbandonUsageHeader } from './hooks/useAbandonUsageHeader';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { GetUsageHeadersVariables } from '@/graphql/contracts/_generated_/getUsageHeaders.query';
import { User } from '@/hooks/identity/responseModels/user';
import { DOCUMENTS_PER_PAGE } from '@/config/common.config';
import { CreateUsageHeaderInputType } from '@/graphql/contracts/_generated_/contracts';
import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import { useRightPanel } from './hooks/useRightPanel';
import FileUpload from './FileUpload/FileUpload';
import { ConfirmationModalBody } from './components/DeleteConfirmationText/ConfirmationModalBody';

const VarBillingComponent = () => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const { createUsageHeader, resetCreateUsageHeader } = useCreateUsageHeader();
  const { searchUsers } = useSearchUsers();
  const gridRef = useRef<EnquiryHandle<ImportDocumentWithId>>(null);
  const { headerColumns } = useHeaderColumns();
  const pageCursorRef = useRef<string>();
  const { getUsageHeaders } = useGetUsageHeaders();
  const { goToVarBillingBySearch } = useVarBillingNavigation();
  const { abandonUsageHeader } = useAbandonUsageHeader();

  const enquiry = useServerEnquiry<ImportDocumentWithId>({
    gridRef: gridRef,
    headerColumns: headerColumns as any,
    initialFilter: [],
  });

  const {
    tabsConfiguration,
    isOpenFileUploadWidget,
    closeWidget,
    isVisibleDeleteConfirmModal,
    closeDeleteConfirmModal,
  } = useRightPanel({
    selectedHeaderRows: enquiry.selectedHeaderRows,
    resetCreateUsageHeader,
  });

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  const getData = async (
    _params: IServerSideGetRowsParams<ImportDocumentWithId>,
    serverSideCriteria: ServerSideCriteria,
  ) => {
    pageCursorRef.current = serverSideCriteria.nextPageCursor;
    const response = await getUsageHeaders({
      after: serverSideCriteria.nextPageCursor,
      first: DOCUMENTS_PER_PAGE,
      filter: {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        ...serverSideCriteria.groupingFilters,
      },
      sort: serverSideCriteria.userSetSorting as GetUsageHeadersVariables['sort'],
    });

    const usageHeaders = response?.data?.usageHeaders;
    const uniqueUploadUserIds = new Set(
      (usageHeaders?.items ?? [])
        .map((item) => item?.uploadedBy)
        .filter((id): id is string => !!id)
    );

    let activeUsers: User[];
    if (uniqueUploadUserIds.size > 0) {
      activeUsers = await searchUsers({ ids: Array.from(uniqueUploadUserIds) as string[] });
    }

    const filesWithMatchedUsers = (usageHeaders?.items ?? [])
      .filter(
        (doc): doc is typeof doc & { uploadedBy: string; iD: any; __typename: 'UsageHeaderOutput' } =>
          !!doc &&
          typeof doc.uploadedBy === 'string' &&
          typeof doc.iD !== 'undefined' &&
          doc.__typename === 'UsageHeaderOutput'
      )
      .map((doc) => {
        const matchedUser = activeUsers?.find((user) => user?.Id === doc?.uploadedBy);
        return {
          ...doc,
          id: doc.iD,
          uploadedBy: matchedUser ? `${matchedUser.FirstName} ${matchedUser.LastName}` : null,
        };
      });
    setPageIsLoading(false);

    const searchIsCleared = enquiry.serverSide?.current?.search === '';
    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToVarBillingBySearch(enquiry.serverSide?.current?.search);
    }

    return {
      items: filesWithMatchedUsers ?? [],
      pageInfo: {
        hasNextPage: usageHeaders?.pageInfo.hasNextPage,
        hasPreviousPage: usageHeaders?.pageInfo.hasPreviousPage,
        endCursor: usageHeaders?.pageInfo?.endCursor,
        startCursor: usageHeaders?.pageInfo?.startCursor,
      },
    };
  };

  const refreshGrid = () => {
    gridRef.current?.deselectRows();
    gridRef.current?.refreshServerSideGrid({
      purge: true,
    });
  };


  const handleFileUpload = (data: { uploadId: string; fileName: string; numberOfRows: number }) => {
    const fileData: CreateUsageHeaderInputType = {
      fileName: data.fileName,
      uploadId: data.uploadId,
      numberOfRows: data.numberOfRows,
    };
    createUsageHeader(fileData, refreshGrid);
  };

  const confirmAbandonUsageHeader = () => {
    const usageHeaderId = enquiry.selectedHeaderRows?.[0]?.data?.iD;

    if (typeof usageHeaderId === 'string') {
      abandonUsageHeader({
        usageHeaderId,
        onSuccess: () => refreshGrid(),
      });
    }
    closeDeleteConfirmModal();
  };

  return (
    <>
      <ContractsEnquiriesScreen
        ref={gridRef}
        enquiry={enquiry}
        formName={'VariableBilling'}
        headerProps={{
          primaryTitle: 'Variable Billing Imports',
          secondaryTitle: 'List',
          searchPlaceholder: 'Search by file name...',
          search: searchTextFromQuery,
          showExportAll: false,
          showExportCurrent: false,
          hideAdvancedFilter: true,
        }}
        detailProps={{
          exportFileName: 'Variable Billing',
          rowGroupPanelShow: 'never',
          getData: getData as any,
          pageSize: DOCUMENTS_PER_PAGE,
          suppressRowClickSelection: false,
          unexpectedBehaviorTitle: 'No lines could be found.',
          unexpectedBehaviorDescription:
            'Please check the search criteria and the filters and try again.',
          loading: pageIsLoading,
        }}
        rightPanelTabs={tabsConfiguration}
      />
      <FileUpload
        onUploadSuccess={handleFileUpload}
        isOpen={isOpenFileUploadWidget}
        onClose={closeWidget}
      />
      <ConfirmationModal
        open={isVisibleDeleteConfirmModal}
        onClose={closeDeleteConfirmModal}
        onConfirm={confirmAbandonUsageHeader}
        confirmButtonText="Confirm"
        title="Please confirm you wish to continue"
        type={ModalType.Warning}
        footerAlign={FlexProps['space-between']}
      >
        {enquiry.selectedHeaderRows?.[0]?.data && (
          <ConfirmationModalBody selectedFile={enquiry.selectedHeaderRows[0].data} />
        )}
      </ConfirmationModal>
    </>
  );
};

export default VarBillingComponent;
